import {
  productionAssetsURL,
  productionR2Origin,
} from '@basehub/shared-constants'
import { hashRepoId } from '@basehub/utils/hash'
import { z } from 'zod'
import * as jose from 'jose'

export const FileProxySession = z.object({
  repoId: z.string(),
  originUrl: z.string(),
})

export type FileProxySession = z.infer<typeof FileProxySession>

// Get the secret from environment variables instead of hardcoding
const getSecret = () => {
  const secret = '91j20jdfa0fdisdkfoakg-q0krgq-r0ke-a-uwehg-whuqe'
  if (!secret) {
    throw new Error('FILE_UPLOADS_JWT_SECRET environment variable is required')
  }
  return secret
}

/**
 * We want our JWT generation to be deterministic so the same input data always
 * produces the same token. This makes the URLs cacheable and consistent.
 */
export const fileProxySessionManager = {
  seal: async (data: FileProxySession) => {
    // No timestamps or expirations to ensure deterministic output
    return await new jose.SignJWT(data)
      .setProtectedHeader({ alg: 'HS256' })
      .sign(new TextEncoder().encode(getSecret()))
  },
  unseal: async (token: string) => {
    // No audience or issuer verification needed
    const { payload } = await jose.jwtVerify(
      token,
      new TextEncoder().encode(getSecret())
    )

    return FileProxySession.parse(payload)
  },
}

export const proxyAsset = async (
  src: string,
  repoId: string,
  opts?: {
    replaceFn?: (originalUrl: URL) => URL
  }
) => {
  let srcReplaced = src
  if (opts?.replaceFn) {
    let srcAsURL: URL
    try {
      srcAsURL = new URL(src)
    } catch (e) {
      return src
    }
    srcAsURL = opts.replaceFn(srcAsURL)
    srcReplaced = srcAsURL.toString()
  }
  const jwt = await fileProxySessionManager.seal({
    originUrl: srcReplaced,
    repoId,
  })
  const mimeType = srcReplaced.split('.').pop()
  return `${productionAssetsURL}/${hashRepoId(repoId)}/__proxy/${jwt}${
    mimeType ? `.${mimeType}` : ''
  }`
}

type JSONContent = {
  type: string
  attrs: Record<string, JSONContent>
  content?: JSONContent[]
  marks: {
    type: string
    attrs: Record<string, JSONContent>
  }[]
  text: string
}

export const proxyRichTextAssets = async (
  json: JSONContent,
  repoId: string,
  opts?: {
    replaceFn?: (originalUrl: URL) => URL
  }
) => {
  const result = await mapJSONNode(json, async (node) => {
    if (node.type === 'image' || node.type === 'video') {
      const src = node.attrs?.src as string | undefined
      if (
        !src ||
        src.includes(productionR2Origin) ||
        src.includes(productionAssetsURL)
      ) {
        return node
      }
      // we wanna proxy these...
      const proxySrc = await proxyAsset(src, repoId, opts)

      return {
        ...node,
        attrs: { ...node.attrs, src: proxySrc },
      } as unknown as JSONContent
    }
    return node
  })
  return result
}

const mapJSONNode = async (
  node: JSONContent,
  fn: (node: JSONContent) => Promise<JSONContent>
): Promise<JSONContent | null> => {
  if (!node.content) {
    return await fn(node)
  } else if (node.content.length === 0) {
    const newNode = await fn(node)
    delete newNode.content
    return newNode
  }
  return {
    ...node,
    content: (
      await Promise.all(node.content.flatMap((child) => mapJSONNode(child, fn)))
    ).filter(Boolean) as JSONContent[],
  } as unknown as JSONContent
}
